import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";

const EMPTY_BRANCH = {
  branch_name: "",
  branch_address: "",
  branch_state: "",
  branch_gst_no: "",
};

const BranchValidationSchema = Yup.object().shape({
  branch_name: Yup.string()
    .trim()
    .max(30, "Branch name must be 30 characters or fewer")
    .required("Branch name is required"),
  branch_address: Yup.string()
    .trim()
    .max(200, "Branch address must be 200 characters or fewer")
    .required("Branch address is required"),
  branch_state: Yup.string()
    .trim()
    .max(20, "Branch state must be 20 characters or fewer")
    .required("Branch state is required"),
  branch_gst_no: Yup.string()
    .trim()
    .max(25, "Branch GST number must be 25 characters or fewer")
    .required("Branch GST number is required"),
});

const FieldError = ({ name }) => (
  <ErrorMessage name={name}>
    {(msg) => (
      <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "4px" }}>
        {msg}
      </div>
    )}
  </ErrorMessage>
);

/**
 * Modal for adding a new branch to a company.
 *
 * Props:
 * - isOpen: boolean
 * - toggle: () => void
 * - companyUid: string - the company this branch belongs to
 * - onSaved: () => void - called after a successful save (e.g. refetch company)
 */
const AddBranchModal = ({ isOpen, toggle, companyUid, onSaved }) => {
  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        company_uid: companyUid,
        ...values,
      };

      const { data } = await axiosInstance.post(
        "/api/partner/addbranch",
        payload
      );

      if (data?.status) {
        toast.success(data?.message || "Branch added successfully", {
          position: "top-center",
        });
        resetForm();
        onSaved && onSaved();
        toggle();
      } else {
        toast.error(data?.message || "Unable to add branch", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "An error occurred", {
        position: "top-center",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} tag="h5">
        Add New Branch
      </ModalHeader>
      <Formik
        initialValues={EMPTY_BRANCH}
        validationSchema={BranchValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <ModalBody>
              <Row>
                <Col md="12">
                  <div className="mb-3">
                    <Label className="form-label">Branch Name *</Label>
                    <Field
                      name="branch_name"
                      type="text"
                      maxLength="50"
                      className={
                        "form-control" +
                        (errors.branch_name && touched.branch_name
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <FieldError name="branch_name" />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <div className="mb-3">
                    <Label className="form-label">Branch Address *</Label>
                    <Field
                      name="branch_address"
                      as="textarea"
                      rows="2"
                      maxLength="500"
                      className={
                        "form-control" +
                        (errors.branch_address && touched.branch_address
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <FieldError name="branch_address" />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Branch State *</Label>
                    <Field
                      name="branch_state"
                      type="text"
                      maxLength="50"
                      className={
                        "form-control" +
                        (errors.branch_state && touched.branch_state
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <FieldError name="branch_state" />
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Branch GST No. *</Label>
                    <Field
                      name="branch_gst_no"
                      type="text"
                      maxLength="50"
                      className={
                        "form-control" +
                        (errors.branch_gst_no && touched.branch_gst_no
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <FieldError name="branch_gst_no" />
                  </div>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                type="button"
                onClick={toggle}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Add Branch"}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddBranchModal;
